import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconBuildingWarehouse, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const Agencies = () => {
    const [appId, setAppId] = useState(null);
    const [agencies, setAgencies] = useState([]);
    const [banks, setBanks] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAgency, setEditingAgency] = useState(null);
    const [form, setForm] = useState({ bank: '', number: '', name: '', address: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadAgencies();
        loadBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadAgencies = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/bank-agencies/`);
            setAgencies(res.data || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar agências' });
        }
    };

    const loadBanks = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/banks/`);
            setBanks(res.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCreate = () => {
        setEditingAgency(null);
        setForm({ bank: '', number: '', name: '', address: '' });
        setDialogOpen(true);
    };

    const handleOpenEdit = (agency) => {
        setEditingAgency(agency);
        setForm({ bank: agency.bank, number: agency.number, name: agency.name || '', address: agency.address || '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingAgency) {
                await api.patch(`v1/apps/${appId}/bank-agencies/${editingAgency.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/bank-agencies/`, form);
            }
            setDialogOpen(false);
            loadAgencies();
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || Object.values(error.response?.data || {})[0] || 'Erro ao salvar agência'
            });
        }
    };

    const handleDelete = async (agency) => {
        if (!window.confirm(`Excluir a agência "${agency.number}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/bank-agencies/${agency.id}/`);
            setAgencies((prev) => prev.filter((a) => a.id !== agency.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir agência' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuildingWarehouse}
                gradient="graphite"
                title="Agências Bancárias"
                subtitle="Agências usadas nas contas bancárias das suas empresas"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Nova agência
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Banco</TableCell>
                                <TableCell>Número</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {agencies.map((agency) => (
                                <TableRow key={agency.id} hover>
                                    <TableCell>
                                        {agency.bank_code} - {agency.bank_name}
                                    </TableCell>
                                    <TableCell>{agency.number}</TableCell>
                                    <TableCell>{agency.name || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(agency)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(agency)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {agencies.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhuma agência cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingAgency ? 'Editar agência' : 'Nova agência'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            fullWidth
                            label="Banco"
                            value={form.bank}
                            onChange={(e) => setForm({ ...form, bank: e.target.value })}
                        >
                            {banks.map((bank) => (
                                <MenuItem key={bank.id} value={bank.id}>
                                    {bank.code} - {bank.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Número da agência"
                            fullWidth
                            value={form.number}
                            onChange={(e) => setForm({ ...form, number: e.target.value })}
                        />
                        <TextField
                            label="Nome (opcional)"
                            fullWidth
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <TextField
                            label="Endereço (opcional)"
                            fullWidth
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Agencies;
